import { Form } from 'antd';
import { useContext } from 'react';

import { ModernHotelContext } from 'app/context/HotelContext/ModernHotelContext';
import { ExtranetPackStatus } from 'app/typings/core/extranet/hotel/packs';

import { calculatePackPricing } from '../_components/calculatePackPricing';

import { useEffectiveRoomDiscount } from './useEffectiveRoomDiscount';

export const MIN_PACK_PRICING_SCORE = 50;

export type PackPricing = {
  packId: number;
  name: string;
  score: number | undefined;
  originalPackPrice: number;
  discountedPackPrice: number;
  discount: number;
};

type UsePackPricingScoresReturn = {
  packPricings: PackPricing[];
  getPackPricing: (packId: number) => PackPricing | undefined;
  lowScorePacks: PackPricing[];
  hasLowScore: boolean;
};

export const usePackPricingScores = (): UsePackPricingScoresReturn => {
  const { packsList, getExperience } = useContext(ModernHotelContext);

  const form = Form.useFormInstance<{
    averageRoomPrice: number;
    defaultNightRoomDiscount: number;
  }>();

  const averageRoomPrice = Form.useWatch('averageRoomPrice', form);
  const effectiveRoomDiscount = useEffectiveRoomDiscount();

  const formValues = (Form.useWatch([], form) ?? {}) as Record<string, number>;

  const overnightPublishedPacks = packsList.filter(
    (pack) => !pack.isDaytime && pack.status !== ExtranetPackStatus.HIDDEN
  );

  const packPricings: PackPricing[] = overnightPublishedPacks.map((pack) => {
    const { score, originalPackPrice, discountedPackPrice, discount } =
      calculatePackPricing(
        pack,
        formValues,
        form,
        averageRoomPrice,
        effectiveRoomDiscount ?? 0,
        getExperience
      );

    return {
      packId: pack.id,
      name: pack.nameForHotel,
      score: averageRoomPrice ? score : undefined,
      originalPackPrice,
      discountedPackPrice,
      discount,
    };
  });

  const getPackPricing = (packId: number) =>
    packPricings.find((pack) => pack.packId === packId);

  const lowScorePacks = packPricings.filter(
    (pack) => pack.score !== undefined && pack.score < MIN_PACK_PRICING_SCORE
  );

  return {
    packPricings,
    getPackPricing,
    lowScorePacks,
    hasLowScore: lowScorePacks.length > 0,
  };
};
